/**
 * Canvas component - renders the wheel using Winwheel.js
 */

import React, { useId, useEffect, useRef } from 'react';
import { useWinwheel } from '../hooks/useWinwheel';
import { SpinnerWheelSpinButton } from './SpinnerWheelSpinButton';
import { DEFAULT_ENTRIES } from '../utils/colors';
import type { SpinnerWheelCanvasProps, WheelSegment } from '../types/wheel';
import './SpinnerWheelCanvas.css';

// Inline SVG strings (embedded directly to work with Rollup library builds)
const wheelRingSvg = `<svg width="512" height="512" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
<circle cx="256" cy="256" r="256" fill="#F3F4F7"/>
<path d="M256 500C390.757 500 500 390.757 500 256C500 121.243 390.757 12 256 12C121.243 12 12 121.243 12 256C12 390.757 121.243 500 256 500Z" fill="white"/>
<path d="M256 500.75C391.172 500.75 500.75 391.172 500.75 256C500.75 120.828 391.172 11.25 256 11.25C120.828 11.25 11.25 120.828 11.25 256C11.25 391.172 120.828 500.75 256 500.75Z" stroke="#D3D7E1" stroke-opacity="0.5" stroke-width="1.5" stroke-miterlimit="10"/>
</svg>`;

const pointerStarSvg = `<svg width="82" height="78" viewBox="0 0 82 78" fill="none" xmlns="http://www.w3.org/2000/svg">
<path fill-rule="evenodd" clip-rule="evenodd" d="M29.3853 49.1702C26.6099 48.5667 24.0276 47.2738 21.8784 45.4115C19.7292 43.5492 18.0828 41.1781 17.0922 38.5183C16.1016 35.8585 15.799 32.9964 16.2123 30.198C16.6257 27.3995 17.7417 24.7555 19.4567 22.5116C21.1718 20.2648 23.4314 18.4915 26.0251 17.3566C28.6189 16.2217 31.4625 15.7622 34.2913 16.0208C37.1201 16.2793 39.8422 17.2476 42.2042 18.8354C44.5663 20.4233 46.4916 22.5791 47.8008 25.1023L61.3918 51.4056L62.3672 53.5174C62.9418 54.7613 61.8582 56.1303 60.5156 55.8567L58.3273 55.4108L29.3853 49.1702ZM17.0389 20.6659C15.0109 23.32 13.6918 26.4463 13.2033 29.7535C12.7147 33.0611 13.0728 36.4412 14.2418 39.5799C15.4107 42.7185 17.3528 45.5148 19.8865 47.7103C22.4203 49.9058 25.4651 51.4306 28.739 52.1425L57.703 58.3878L59.9082 58.8371C63.6935 59.6084 66.7484 55.7489 65.1286 52.2419L64.125 50.0691L50.5031 23.706L50.5007 23.7013C48.9562 20.7249 46.6859 18.183 43.9012 16.3111C41.1165 14.4391 37.9062 13.2968 34.5681 12.9917C31.2299 12.6866 27.8715 13.2286 24.8059 14.57C21.7406 15.9112 19.0687 18.0075 17.0399 20.6646" fill="#0A0A0A" fill-opacity="0.1"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M29.3853 47.1702C26.6099 46.5667 24.0276 45.2738 21.8784 43.4115C19.7292 41.5492 18.0828 39.1781 17.0922 36.5183C16.1016 33.8585 15.799 30.9964 16.2123 28.198C16.6257 25.3995 17.7417 22.7555 19.4567 20.5116C21.1718 18.2648 23.4314 16.4915 26.0251 15.3566C28.6189 14.2217 31.4625 13.7622 34.2913 14.0208C37.1201 14.2793 39.8422 15.2476 42.2042 16.8354C44.5663 18.4233 46.4916 20.5791 47.8008 23.1023L61.3918 49.4056L62.3672 51.5174C62.9418 52.7613 61.8582 54.1303 60.5156 53.8567L58.3273 53.4108L29.3853 47.1702ZM17.0389 18.6659C15.0109 21.32 13.6918 24.4463 13.2033 27.7535C12.7147 31.0611 13.0728 34.4412 14.2418 37.5799C15.4107 40.7185 17.3528 43.5148 19.8865 45.7103C22.4203 47.9058 25.4651 49.4306 28.739 50.1425L57.703 56.3878L59.9082 56.8371C63.6935 57.6084 66.7484 53.7489 65.1286 50.2419L64.125 48.0691L50.5031 21.706L50.5007 21.7013C48.9562 18.7249 46.6859 16.183 43.9012 14.3111C41.1165 12.4391 37.9062 11.2968 34.5681 10.9917C31.2299 10.6866 27.8715 11.2286 24.8059 12.57C21.7406 13.9112 19.0687 16.0075 17.0399 18.6646" fill="white"/>
<path d="M16.2125 28.198C16.6258 25.3995 17.7418 22.7555 19.4568 20.5116C21.1719 18.2648 23.4315 16.4915 26.0253 15.3566C28.619 14.2218 31.4626 13.7622 34.2914 14.0208C37.1202 14.2793 39.8423 15.2476 42.2044 16.8354C44.5664 18.4233 46.4917 20.5791 47.801 23.1023L61.392 49.4056L62.3674 51.5174C62.9419 52.7613 61.8583 54.1303 60.5157 53.8567L58.3274 53.4108L29.3855 47.1702C26.61 46.5667 24.0277 45.2738 21.8785 43.4115C19.7293 41.5493 18.083 39.1781 17.0924 36.5183C16.1018 33.8585 15.7991 30.9964 16.2125 28.198Z" fill="#89BEFA"/>
<path d="M43.3203 38.6575C47.7224 32.9041 46.6275 24.6718 40.8747 20.27C35.1218 15.8683 26.8896 16.964 22.4874 22.7173C18.0853 28.4707 19.1802 36.703 24.933 41.1048C30.6859 45.5065 38.9181 44.4109 43.3203 38.6575Z" fill="#348EF6"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M40.5655 20.0403C43.8444 24.6088 43.9151 30.9596 40.3205 35.6575C35.9967 41.3085 27.9778 42.4661 22.2427 38.3346C22.9793 39.3609 23.8779 40.2973 24.9333 41.1048C30.6861 45.5066 38.9183 44.4109 43.3205 38.6575C47.7227 32.9041 46.6277 24.6718 40.8749 20.27C40.7725 20.1917 40.6694 20.1151 40.5655 20.0403Z" fill="#257ADC"/>
<path d="M38.6959 28.6489C38.6184 28.4022 38.4081 28.2262 38.1603 28.2017L34.7935 27.8664L33.4469 24.6098C33.3477 24.3711 33.1234 24.2158 32.8752 24.2146C32.6269 24.2134 32.4042 24.3665 32.3072 24.6048L30.9924 27.8478L27.6282 28.1502C27.381 28.1729 27.173 28.3463 27.0974 28.5923C27.0219 28.8382 27.0941 29.1088 27.282 29.2801L29.8371 31.6204L29.1038 35.0645C29.0502 35.3177 29.1458 35.5802 29.3481 35.7333C29.4568 35.8155 29.5838 35.8577 29.7116 35.8583C29.8218 35.8589 29.931 35.8284 30.0288 35.7676L32.9228 33.9714L35.8334 35.796C36.0464 35.9303 36.314 35.9194 36.5144 35.7683C36.7153 35.6168 36.8083 35.3551 36.7521 35.1019L35.9852 31.6504L38.5175 29.3354C38.7038 29.1655 38.7739 28.8961 38.6959 28.6489Z" fill="white"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M29.8371 31.6204L27.282 29.2801C27.0941 29.1088 27.0219 28.8383 27.0975 28.5923C27.173 28.3463 27.3811 28.1729 27.6282 28.1502L30.9924 27.8478L32.3073 24.6048C32.4042 24.3665 32.6269 24.2134 32.8752 24.2146C33.1235 24.2159 33.3477 24.3711 33.447 24.6098L34.7935 27.8664L38.1603 28.2017C38.4082 28.2262 38.6184 28.4022 38.696 28.6489C38.7739 28.8961 38.7038 29.1655 38.5176 29.3354L35.9852 31.6504L36.7522 35.1019C36.8083 35.3551 36.7154 35.6168 36.5144 35.7683C36.3141 35.9195 36.0464 35.9303 35.8335 35.796L32.9229 33.9714L30.0289 35.7676C29.9311 35.8284 29.8219 35.8589 29.7117 35.8583C29.5838 35.8577 29.4569 35.8155 29.3481 35.7333C29.1458 35.5803 29.0502 35.3177 29.1039 35.0645L29.8371 31.6204ZM32.9313 35.7286L30.9686 36.9467C30.6134 37.1672 30.181 37.2974 29.7187 37.2952C29.1818 37.2926 28.6869 37.1129 28.3027 36.8225C27.6006 36.2914 27.3625 35.4829 27.5101 34.786L28.0875 32.0741L26.1249 30.2765C26.1243 30.2759 26.1237 30.2754 26.123 30.2748C25.4957 29.7018 25.321 28.8912 25.5326 28.2022C25.7465 27.5061 26.412 26.8157 27.4458 26.721L27.4489 26.7207L29.807 26.5087L30.7813 24.1057C31.0619 23.4159 31.8097 22.7726 32.8682 22.7778C33.9259 22.783 34.68 23.4328 34.9677 24.1245L34.9688 24.1272L35.966 26.5388L38.3245 26.7737C38.3246 26.7737 38.3243 26.7737 38.3245 26.7737C38.3247 26.7737 38.3255 26.7738 38.3257 26.7738C39.3632 26.8769 40.0371 27.576 40.2573 28.2748M32.9313 35.7286L34.9019 36.9638C34.9027 36.9644 34.9035 36.9649 34.9043 36.9654C35.7383 37.49 36.8232 37.4315 37.5725 36.8663C38.2672 36.3423 38.4981 35.5374 38.3432 34.8383L37.7394 32.1213L39.686 30.3418C39.6858 30.342 39.6863 30.3416 39.686 30.3418C39.6862 30.3417 39.6871 30.3409 39.6872 30.3407C40.3054 29.776 40.476 28.9698 40.2577 28.2762" fill="white" fill-opacity="0.4"/>
</svg>`;

export const SpinnerWheelCanvas: React.FC<SpinnerWheelCanvasProps> = ({
  entries,
  isSpinning,
  canvasSize = 465,
  pointerAngle = 309,
  colors,
  colorTheme = 'default',
  spinDuration = 10,
  spinRevolutions = 7,
  onSpinComplete,
  onSegmentChange,
  onTickSound,
  onSpin,
  className = '',
  ringImage,
  pointerImage,
}) => {
  const uniqueId = useId().replace(/:/g, '');
  const canvasId = `wheel-canvas-${uniqueId}`;
  const hasInitiatedSpinRef = useRef(false);
  const winnerIndexRef = useRef(-1);

  // Display entries (show defaults if empty)
  const displayEntries = entries.length > 0 ? entries : DEFAULT_ENTRIES;

  // Handle spin completion - map segment back to entry
  const handleSpinComplete = (segment: WheelSegment) => {
    if (onSpinComplete) {
      const winnerEntry = displayEntries[winnerIndexRef.current];
      if (winnerEntry) {
        // Always include the actual winner's name in fullText
        // This is needed for >60 entries case where segments have no text
        onSpinComplete({
          ...segment,
          fullText: winnerEntry.name,
          text: winnerEntry.name,
        });
      }
    }
  };

  // Handle segment change during spin
  const handleSegmentChange = (segment: WheelSegment) => {
    if (onSegmentChange) {
      // For >60 entries, segment.text is empty, so we just pass the color
      // For <=60 entries, segment has text from the entry
      onSegmentChange(segment);
    }
  };

  const { startRealSpin } = useWinwheel({
    canvasId,
    entries: displayEntries,
    canvasSize,
    pointerAngle,
    colors,
    colorTheme,
    spinDuration,
    spinRevolutions,
    onSegmentChange: handleSegmentChange,
    onSpinComplete: handleSpinComplete,
    onTickSound,
  });

  // Handle spin state changes
  useEffect(() => {
    if (isSpinning && !hasInitiatedSpinRef.current) {
      hasInitiatedSpinRef.current = true;
      // Select random winner
      winnerIndexRef.current = Math.floor(Math.random() * displayEntries.length);
      startRealSpin(winnerIndexRef.current);
    } else if (!isSpinning && hasInitiatedSpinRef.current) {
      hasInitiatedSpinRef.current = false;
    }
  }, [isSpinning, displayEntries.length, startRealSpin]);

  const handleSpinClick = () => {
    if (isSpinning || displayEntries.length === 0) return;
    onSpin?.();
  };

  return (
    <div className={`wheel-canvas ${isSpinning ? 'is-spinning' : ''} ${className}`}>
      {/* White circular background (visible on narrow screens) */}
      <div className="bg-spinner-wheel" />

      {/* Wheel ring background - inline SVG or custom image */}
      {ringImage ? (
        <img className="wheel-ring" src={ringImage} alt="wheel background" />
      ) : (
        <div
          className="wheel-ring"
          dangerouslySetInnerHTML={{ __html: wheelRingSvg }}
          aria-label="wheel background"
        />
      )}

      {/* Star pointer indicator - inline SVG or custom image */}
      {pointerImage ? (
        <img className="wheel-pointer" src={pointerImage} alt="pointer" />
      ) : (
        <div
          className="wheel-pointer"
          dangerouslySetInnerHTML={{ __html: pointerStarSvg }}
          aria-label="pointer"
        />
      )}

      {/* Spin button in center */}
      <div className="spin-btn-wrap">
        <SpinnerWheelSpinButton disabled={isSpinning} onClick={handleSpinClick} />
      </div>

      {/* Canvas for wheel rendering */}
      <div
        className="wheel-canvas__rotor"
        data-wheel-rotator="true"
        style={{ width: canvasSize, height: canvasSize }}
      >
        <img
          className="wheel-canvas__face"
          data-wheel-face="true"
          alt=""
          aria-hidden="true"
        />
        <canvas id={canvasId} width={canvasSize} height={canvasSize}>
          <p className="canvas-fallback">
            Sorry, your browser doesn't support canvas. Please try another.
          </p>
        </canvas>
      </div>
    </div>
  );
};

export default SpinnerWheelCanvas;
